import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface CollapsibleCardProps extends Omit<React.ComponentProps<typeof Card>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  defaultOpen?: boolean;
  headerAction?: React.ReactNode;
  footer?: React.ReactNode;
  collapseButtonPosition?: "header" | "title";
  collapsible?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export function CollapsibleCard({
  title,
  description,
  defaultOpen = true,
  headerAction,
  footer,
  collapseButtonPosition = "header",
  collapsible = true,
  onOpenChange,
  className,
  children,
  ...props
}: CollapsibleCardProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  // If not collapsible, render a standard card
  if (!collapsible) {
    return (
      <Card className={className} {...props}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          {headerAction && <div>{headerAction}</div>}
        </CardHeader>
        <CardContent>{children}</CardContent>
        {footer && <CardFooter>{footer}</CardFooter>}
      </Card>
    );
  }

  return (
    <Card className={cn("overflow-hidden", className)} {...props}>
      <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            {collapseButtonPosition === "title" && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <span className="sr-only">Toggle content</span>
                </Button>
              </CollapsibleTrigger>
            )}
            <div>
              <CardTitle className="text-sm font-medium">{title}</CardTitle>
              {description && <CardDescription>{description}</CardDescription>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {headerAction}
            {collapseButtonPosition === "header" && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <span className="sr-only">Toggle content</span>
                </Button>
              </CollapsibleTrigger>
            )}
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>{children}</CardContent>
          {footer && <CardFooter>{footer}</CardFooter>}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
